import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { FAQ_ICONS, getFaqIconMeta, DEFAULT_FAQ_ICON_KEY } from '../../constants/faqIcons';

const CATEGORIES = ['Todas', 'General', 'Entregas', 'Pagos', 'Cuenta', 'Pedidos'];

const FaqIconPicker = ({ value, onChange, isDark }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const dropdownRef = useRef(null);

  const currentMeta = useMemo(() => {
    return getFaqIconMeta(value || DEFAULT_FAQ_ICON_KEY);
  }, [value]);

  // Filtered list based on search and category
  const filteredIcons = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return FAQ_ICONS.filter((item) => {
      const matchCategory = selectedCategory === 'Todas' || item.category === selectedCategory;
      const matchSearch =
        !term ||
        item.label.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term);
      return matchCategory && matchSearch;
    });
  }, [searchTerm, selectedCategory]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const popoverBg = isDark ? '#1e293b' : '#ffffff';
  const popoverBorder = isDark ? '1px solid #334155' : '1px solid #e2e8f0';
  const itemDefaultBg = isDark ? '#0f172a' : '#f8fafc';
  const itemHoverBg = isDark ? '#27354f' : '#f1f5f9';
  const activeBg = isDark ? '#1e3a8a' : '#dbeafe';

  return (
    <div className="faq-icon-picker-container position-relative" ref={dropdownRef}>
      {/* TRIGGER BUTTON (SELECCIÓN ACTUAL) */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="d-flex align-items-center justify-content-between p-2 px-3 rounded cursor-pointer"
        style={{
          border: isDark ? '1.5px solid #334155' : '1.5px solid #dcdfe6',
          backgroundColor: isDark ? '#0f172a' : '#f8fafc',
          transition: 'all 0.2s ease',
          minHeight: '46px',
        }}
        title="Clic para seleccionar el ícono representativo"
      >
        <div className="d-flex align-items-center gap-3 overflow-hidden">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              backgroundColor: '#183eeb',
              color: '#ffffff',
              fontSize: '1.25rem',
            }}
          >
            <i className={`mdi ${currentMeta.mdi}`}></i>
          </div>
          <div className="text-truncate">
            <div className="fw-bold small" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
              {currentMeta.label}
            </div>
            <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
              Categoría: <span className="fw-semibold" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{currentMeta.category}</span>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 ms-2 flex-shrink-0">
          <span
            className="badge rounded-pill px-2 py-1"
            style={{
              backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
              color: isDark ? '#cbd5e1' : '#475569',
              fontSize: '0.72rem',
              fontWeight: '500',
            }}
          >
            Cambiar
          </span>
          <i className={`mdi ${isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'} text-muted`}></i>
        </div>
      </div>

      {/* DROPDOWN POPOVER */}
      {isOpen && (
        <div
          className="shadow-lg rounded p-3 position-absolute"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: popoverBg,
            border: popoverBorder,
            height: '370px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* BUSCADOR */}
          <div className="mb-2">
            <div className="position-relative">
              <Form.Control
                size="sm"
                type="text"
                placeholder="Buscar ícono (ej: envíos, pagos, horarios, cuenta)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  paddingLeft: '34px',
                  borderRadius: '6px',
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  color: isDark ? '#ffffff' : '#000000',
                  borderColor: isDark ? '#475569' : '#cbd5e1',
                  height: '36px',
                  fontSize: '0.82rem',
                }}
              />
              <i
                className="mdi mdi-magnify position-absolute text-muted"
                style={{
                  left: '11px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '1.1rem',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              ></i>
            </div>
          </div>

          {/* SELECTORES DE CATEGORÍA (PILLS CON ALINEACIÓN PERFECTA) */}
          <div
            className="d-flex gap-2 align-items-center mb-2 pb-1"
            style={{
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              padding: '2px 0',
              flexShrink: 0,
            }}
          >
            {CATEGORIES.map((cat) => {
              const isCatActive = selectedCategory === cat;
              return (
                <button
                  type="button"
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: isCatActive ? '600' : '500',
                    padding: '0 12px',
                    borderRadius: '20px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    height: '28px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: isCatActive
                      ? '1px solid #183eeb'
                      : isDark
                      ? '1px solid #334155'
                      : '1px solid #e2e8f0',
                    backgroundColor: isCatActive
                      ? '#183eeb'
                      : isDark
                      ? '#0f172a'
                      : '#f1f5f9',
                    color: isCatActive
                      ? '#ffffff'
                      : isDark
                      ? '#94a3b8'
                      : '#475569',
                    transition: 'all 0.15s ease',
                    boxShadow: isCatActive ? '0 2px 4px rgba(24, 62, 235, 0.25)' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isCatActive) {
                      e.currentTarget.style.backgroundColor = isDark ? '#334155' : '#e2e8f0';
                      e.currentTarget.style.color = isDark ? '#f8fafc' : '#0f172a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCatActive) {
                      e.currentTarget.style.backgroundColor = isDark ? '#0f172a' : '#f1f5f9';
                      e.currentTarget.style.color = isDark ? '#94a3b8' : '#475569';
                    }
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* CUADRÍCULA DE ÍCONOS (SIN NOMBRES TÉCNICOS) */}
          <div
            className="overflow-auto pe-1"
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: '8px',
              alignContent: 'start',
            }}
          >
            {filteredIcons.length === 0 ? (
              <div className="text-center py-4 text-muted small" style={{ gridColumn: '1 / -1' }}>
                <i className="mdi mdi-emoticon-sad-outline d-block h4 mb-1"></i>
                No se encontraron íconos para "{searchTerm}"
              </div>
            ) : (
              filteredIcons.map((item) => {
                const isSelected = item.key === currentMeta.key;
                return (
                  <div
                    key={item.key}
                    onClick={() => {
                      onChange(item.key);
                      setIsOpen(false);
                    }}
                    className="p-2 rounded cursor-pointer text-center d-flex flex-column align-items-center justify-content-center"
                    style={{
                      backgroundColor: isSelected ? activeBg : itemDefaultBg,
                      border: isSelected
                        ? '1.5px solid #183eeb'
                        : isDark
                        ? '1px solid #334155'
                        : '1px solid #e2e8f0',
                      transition: 'all 0.15s ease',
                      height: '76px',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = itemHoverBg;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = itemDefaultBg;
                    }}
                    title={item.label}
                  >
                    <i
                      className={`mdi ${item.mdi} mb-1`}
                      style={{
                        fontSize: '1.45rem',
                        color: isSelected ? '#183eeb' : isDark ? '#94a3b8' : '#475569',
                      }}
                    ></i>
                    <div
                      className="text-truncate w-100 fw-semibold"
                      style={{
                        fontSize: '0.74rem',
                        color: isSelected ? '#183eeb' : isDark ? '#f1f5f9' : '#1e293b',
                      }}
                    >
                      {item.label.split('/')[0].trim()}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqIconPicker;
